import { create } from "zustand";
import { persist } from "zustand/middleware";

/** Which writing engine the studio uses for annotate/write surfaces.
 *
 * - "legacy" — the classic Fabric-based engine and the DEFAULT: per-stroke
 *   markup, paged or infinite canvases with paper styles, the annotation
 *   toolbar with its pen, shapes and paint bucket. This is the product.
 * - "excalidraw" — the Excalidraw editor: one infinite scene per document,
 *   scene-based persistence. Kept as an option for anyone who wants it.
 *
 * The identifier `"legacy"` is a misnomer kept for wire and storage
 * compatibility — it is the value the `documents.canvas_engine` column already
 * holds. Nothing about it is deprecated; the UI calls the same engine
 * "Classic" (settings/SettingsOverlay.tsx).
 *
 * The preference decides the DEFAULT for new notes and for PDFs that carry no
 * markup yet. A document that already has content always reopens in the engine
 * that wrote it (canvas notes via their persisted `canvas_engine`; PDFs by
 * which store holds their ink) — switching the preference never strands or
 * hides existing writing. Persisted per device. */
export type WritingEngine = "excalidraw" | "legacy";

interface WritingEngineState {
  engine: WritingEngine;
  setEngine: (engine: WritingEngine) => void;
}

export const useWritingEngine = create<WritingEngineState>()(
  persist(
    (set) => ({
      engine: "legacy",
      setEngine: (engine) => set({ engine }),
    }),
    {
      name: "graphmind.writing-engine",
      // v1 flips the default from Excalidraw to classic. The stored value from
      // v0 is discarded rather than honoured: it was written by merely opening
      // a device (the old default persisted itself), so keeping it would leave
      // every existing install on the engine this change is stepping back from,
      // with no way to tell an actual preference from an inherited default.
      // Choosing Excalidraw again is one click in Settings, and any note
      // already written in it still reopens there — the preference only picks
      // the engine for NEW surfaces.
      version: 1,
      migrate: (_state, from) => (from >= 1 ? (_state as WritingEngineState) : { engine: "legacy" }),
    },
  ),
);

/** Resolve which engine should render a document's annotate/write surface.
 *
 * Content wins over preference: a canvas note reopens in the engine that owns
 * it (its persisted `canvas_engine`, with pre-toggle notes defaulting to
 * legacy — that engine wrote them); a PDF follows whichever store already
 * holds its ink. Only a genuinely untouched surface follows the preference. */
export function resolveEngine(opts: {
  preference: WritingEngine;
  /** DocumentOut.canvas_engine for canvas notes (undefined for PDFs). */
  canvasEngine?: string | null;
  isCanvas: boolean;
  /** Whether the classic store has rows for this doc (undefined = unknown/loading). */
  hasLegacyMarkup?: boolean;
  /** Whether the drawing scene has any elements (undefined = unknown/loading). */
  hasSceneContent?: boolean;
}): WritingEngine {
  if (opts.isCanvas) {
    if (opts.canvasEngine === "excalidraw") return "excalidraw";
    if (opts.canvasEngine === "legacy") return "legacy";
    return "legacy"; // pre-toggle canvases were written by the classic engine
  }
  if (opts.hasLegacyMarkup) return "legacy";
  if (opts.hasSceneContent) return "excalidraw";
  return opts.preference;
}
